import { forwardRef, useState, useEffect } from 'react'
import { NumericFormat } from 'react-number-format'
import { Input, SizableText, XStack } from 'tamagui'

// Base Tamagui input for NumericFormat to use as customInput
const TamaguiInputBase = forwardRef<HTMLInputElement, any>((props, ref) => {
    const { size, height, error, borderColor, focusStyle, noBorder, ...otherProps } = props

    if (noBorder) {
        return (
            <Input
                size={size ?? "$4"}
                flex={1}
                borderWidth={0}
                borderColor="transparent"
                backgroundColor="transparent"
                padding={0}
                hoverStyle={{ borderWidth: 0, borderColor: 'transparent' }}
                focusStyle={{ borderWidth: 0, borderColor: 'transparent', outlineWidth: 0 }}
                pressStyle={{ borderWidth: 0, borderColor: 'transparent' }}
                {...otherProps}
                ref={ref}
            />
        )
    }

    return (
        <Input
            size={size ?? "$4"}
            height={height ?? 44}
            width="100%"
            borderRadius="$4"
            borderColor={borderColor ?? (error ? "$statusDanger" : undefined)}
            hoverStyle={{
                borderColor: error ? "$statusDanger" : "$borderColorHover",
            }}
            focusStyle={focusStyle ?? {
                borderColor: error ? "$statusDanger" : "$borderColorFocus",
                outlineWidth: 0,
            }}
            {...otherProps}
            ref={ref}
        />
    )
})

interface NumericInputProps {
    value?: string | number | null
    onChange?: (value: number | null) => void
    onBlur?: (e: any) => void
    prefix?: string // e.g., '$' for dollar type
    suffix?: string // e.g., '%' for percentages
    disabled?: boolean
    readOnly?: boolean
    name?: string
    placeholder?: string
    /** Tamagui size token (default: "$4") */
    size?: string
    /** Input height in pixels (default: 44) */
    height?: number
    /** Error state for styling */
    error?: boolean | any
    [key: string]: any
}

/**
 * NumericInput - A specialized input component for numeric values with formatting
 *
 * Wraps react-number-format's NumericFormat component with Tamagui styling.
 * Handles thousand separators, prefix/suffix decorators, and trailing decimal support.
 */
export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
    ({ value, onChange, onBlur, prefix, suffix, disabled, readOnly, name, size, height, error, ...props }, ref) => {
        // Only track trailing decimal override (e.g., "5." while user is still typing)
        // Avoids the stale-state oscillation that useState+useEffect sync causes with rapid typing
        const [trailingDecimal, setTrailingDecimal] = useState<string | null>(null)

        // Clear trailing decimal when parent value changes externally (e.g., form reset)
        useEffect(() => {
            setTrailingDecimal(null)
        }, [value])

        // Use trailing decimal string when active, otherwise pass parent value directly
        const displayValue = trailingDecimal ?? value

        const handleChange = (values: { value: string; floatValue?: number }) => {
            if (!onChange) return

            const val = values.value

            // Support trailing decimal point for better UX
            // e.g., user types "5." - we preserve it visually but don't call onChange yet
            if (typeof val === 'string' && val.endsWith('.')) {
                setTrailingDecimal(val)
            } else {
                if (trailingDecimal !== null) setTrailingDecimal(null)
                // Convert to number or null for parent component
                onChange(values.floatValue !== undefined ? values.floatValue : null)
            }
        }

        const readOnlyStyle = readOnly
            ? {
                  cursor: 'default' as const,
                  pointerEvents: 'none' as const,
                  opacity: 0.6,
              }
            : {}

        const startContent = prefix ? <SizableText>{prefix}</SizableText> : null
        const endContent = suffix ? <SizableText>{suffix}</SizableText> : null
        const showDecorators = startContent || endContent

        const numericFormatInput = (
            <NumericFormat
                {...props}
                value={displayValue as any}
                getInputRef={ref}
                onValueChange={handleChange}
                onBlur={onBlur}
                thousandSeparator
                valueIsNumericString
                customInput={TamaguiInputBase}
                disabled={disabled}
                readOnly={readOnly}
                style={readOnlyStyle}
                // Pass Tamagui props to TamaguiInputBase
                size={size}
                height={height}
                error={error}
            />
        )

        if (showDecorators) {
            return (
                <XStack
                    alignItems="center"
                    gap="$2"
                    width="100%"
                    height={height ?? 44}
                    borderWidth={1}
                    borderColor={error ? '$statusDanger' : '$borderColor'}
                    borderRadius="$4"
                    backgroundColor="$backgroundStrong"
                    paddingHorizontal="$3.5"
                    hoverStyle={{
                        borderColor: error ? '$statusDanger' : '$borderColorHover',
                    }}
                    focusWithinStyle={{
                        borderColor: error ? '$statusDanger' : '$borderColorFocus',
                    }}
                >
                    {startContent && <SizableText color="$colorMuted">{startContent}</SizableText>}
                    <NumericFormat
                        {...props}
                        value={displayValue as any}
                        getInputRef={ref}
                        onValueChange={handleChange}
                        onBlur={onBlur}
                        thousandSeparator
                        valueIsNumericString
                        customInput={TamaguiInputBase}
                        disabled={disabled}
                        readOnly={readOnly}
                        style={readOnlyStyle}
                        size={size}
                        height={height}
                        error={error}
                        noBorder
                    />
                    {endContent && <SizableText color="$colorMuted">{endContent}</SizableText>}
                </XStack>
            )
        }

        return numericFormatInput
    }
)

NumericInput.displayName = 'NumericInput'
